import React, {memo, useEffect, useState} from "react";
import {useDispatch} from "react-redux";
import {css, Theme} from "@emotion/react";
import styled from "@emotion/styled";

import {Button} from "@pg-design/button-module";
import {mb, pb} from "@pg-design/helpers-css";
import {Highlight, Text} from "@pg-design/text-module";
import {fromQueryValues, QueryValues} from "@pg-mono/form";
import {isEmpty, omit, pick} from "@pg-mono/nodash";
import {numberFormat} from "@pg-mono/string-utils";

import {useShouldShowPrice} from "../../../../hooks/use_should_show_price";
import {propertyListSortOptions} from "../../../../property/constants/property_list_sort_options";
import {IPropertyListObj} from "../../../../property_list/hooks/use_property_list";
import {ISearchFiltersFormValues} from "../../../../property_list/PropertyListFilters";
import {PropertyListType} from "../../../../property_list/PropertyListTabSwitcher";
import {Country} from "../../../../region/types/Country";
import {getLocalStoragePropertyFilters, updateLocalStoragePropertyFilters} from "../../../../search/utils/use_localstorage_property_filters";
import {FilterPresets, useUserPreferencesFilters} from "../../../../user/hooks/use_user_preferences_filters";
import {getUserPreferencesToOmit} from "../../../../user/utils/get_user_preferences_to_omit";
import {getCurrency} from "../../../../utils/get_currency";
import {getPlanner3dData} from "../../../constants/get_planner_3d_data";
import {getOfferTypeNamePlural} from "../../../helpers/OfferType";
import {IOfferDetail} from "../../../types/IOfferDetail";
import {OfferPriceType} from "../../../types/OfferPriceType";
import {OfferType} from "../../../types/OfferType";
import {fetchOfferDetailLowestPriceProperty} from "../../actions/fetch_offer_detail_lowest_price_property";
import {fetchOfferDetailProperties, PropertyListTabTypes} from "../../actions/fetch_offer_detail_properties";
import {OFFER_DETAIL_PROPERTY_LIST_DEFAULT_PAGE_SIZE} from "../../constants/offer_detail_properties";
import {Planner3dModal} from "../../planner3D/components/Planner3D";
import {OfferDetailPropertyListFilters} from "./OfferDetailPropertyListFilters";
import {OfferDetailPropertyListTable, propertyListTableFiltersFields} from "./OfferDetailPropertyListTable";
import {OfferDetailPropertyListTiles} from "./OfferDetailPropertyListTiles";

interface IProps {
    offerId: number;
    offer: IOfferDetail;
    propertyListObj: IPropertyListObj;
    onFilterValuesChange: (values: ISearchFiltersFormValues) => void;
    propertyListType?: PropertyListType;
    gtmMeta?: {
        listId: string;
        listName: string;
    };
}

export interface IMainImage {
    m_img_224x147?: string;
    m_img_375x211?: string;
    m_img_760x428?: string;
    m_img_1500?: string;
    m_img_155x87?: string;
}

export const initialPropertiesFilterValues: ISearchFiltersFormValues = {
    area: {lower: "", upper: ""},
    price: {lower: "", upper: ""},
    rooms: {lower: "", upper: ""},
    floor_choices: [],
    house_storeys: []
};

export const OfferDetailPropertyList: React.FC<IProps> = memo((props) => {
    const [currentPropertyListType, setCurrentPropertyListType] = useState(props.propertyListType ?? PropertyListType.TABLE);
    const isApartment = props.offer?.is_holiday_location || props.offer?.is_condohotel;
    const [filterValues, setFilterValues] = useState(initialPropertiesFilterValues);

    const hasPlanner3D = Boolean(getPlanner3dData(props.offer.id));
    const [showPropertyListBlend, setShowPropertyListBlend] = useState(hasPlanner3D);

    const dispatch = useDispatch();

    const userPreferencesFilters = useUserPreferencesFilters(FilterPresets.WithoutRegion);
    /*
     * filter values for property list - either based on user preferences or temporary filters from local storage
     */
    const localStoragePropertyFilters = getLocalStoragePropertyFilters();
    const initialQueryValues: QueryValues = isEmpty(localStoragePropertyFilters) ? userPreferencesFilters : localStoragePropertyFilters;

    /*
        Initial clientside data fetch
    */
    useEffect(() => {
        // set property list filters based on user preferences
        const clearedInitialQueryValue = pick(initialQueryValues, [
            "area_0",
            "area_1",
            "rooms_0",
            "rooms_1",
            "price_1",
            ...(props.offer.type === OfferType.FLAT ? ["floor_choices"] : []),
            ...(props.offer.type === OfferType.HOUSE ? ["house_storeys"] : [])
        ]);

        const searchFilters = fromQueryValues<ISearchFiltersFormValues>(propertyListTableFiltersFields, clearedInitialQueryValue);

        setFilterValues(searchFilters);

        if (props.offerId) {
            // fetch filtered properties only when initial filters has been set
            if (!isEmpty(initialQueryValues)) {
                const defaultQueryValues = {page: 1};
                const clearedInitialQueryValues = omit(initialQueryValues, getUserPreferencesToOmit(props.offer?.type));

                fetchOfferDetailProperties(props.offerId, PropertyListTabTypes.FILTER, OFFER_DETAIL_PROPERTY_LIST_DEFAULT_PAGE_SIZE, defaultQueryValues, {
                    ...clearedInitialQueryValues,
                    type: props.offer.type
                })(dispatch);

                dispatch(fetchOfferDetailLowestPriceProperty(props.offerId));
            }

            // clear ls stored filter after fetching, we don't want to reuse it
            updateLocalStoragePropertyFilters({});
        }
    }, []);

    useEffect(() => {
        props.onFilterValuesChange(filterValues);
    }, [filterValues]);

    const getOfferTypeName = () => {
        const offerTypeName = getOfferTypeNamePlural(props.offer.type);
        return offerTypeName.charAt(0).toUpperCase() + offerTypeName.slice(1);
    };

    const offerRangePrice =
        (props.offer.region.country === Country.SPAIN && props.offer?.stats?.ranges_price_min) || props.offer.stats?.ranges_price_m2_min || null;

    const priceType = props.offer.region.country === Country.SPAIN && props.offer.price_type === OfferPriceType.NETTO ? " netto" : "";

    const shouldShowPrice = useShouldShowPrice({
        offerPriceVisibility: props.offer.configuration.price_visibility,
        vendorPriceVisibility: props.offer.vendor.configuration.price_visibility,
        vendorId: props.offer.vendor.id
    });

    const propertyListHeadline = `${isApartment ? "Apartamenty" : getOfferTypeName()} w inwestycji`;
    const propertyListName = props.gtmMeta?.listName ?? propertyListHeadline;
    const propertyListId = props.gtmMeta?.listId ?? `${isApartment ? "apartamenty" : getOfferTypeName().toLowerCase()}_w_inwestycji`;

    return (
        <>
            <PropertyListWrapper showBlend={showPropertyListBlend && props.propertyListObj.properties?.length > 0}>
                {props.offer && (
                    <>
                        <Text variant="headline_4" as="h2" css={[pb(1)]}>
                            {propertyListHeadline}
                            {` (${props.offer.stats?.properties_count_for_sale})`}
                        </Text>

                        {shouldShowPrice &&
                            (props.offer.region.country === Country.SPAIN || props.offer.region.country === Country.POLAND) &&
                            offerRangePrice && (
                                <Text variant="headline_5" as="h3" css={[pb(2)]}>
                                    od{" "}
                                    <span className="bt">
                                        <Highlight>
                                            <Text variant="headline_2" as="span">{`${numberFormat(offerRangePrice)}${priceType} ${getCurrency({
                                                currency: props.offer.currency
                                            })}`}</Text>
                                            {props.offer.region.country === Country.SPAIN && props.offer.stats?.ranges_price_min ? null : (
                                                <>
                                                    /m<sup>2</sup>
                                                </>
                                            )}
                                        </Highlight>
                                    </span>
                                </Text>
                            )}
                    </>
                )}
                <OfferDetailPropertyListFilters
                    propertyListObj={props.propertyListObj}
                    filterValues={filterValues}
                    offerId={props.offerId}
                    offer={props.offer}
                    setFilterValues={setFilterValues}
                    sortOptions={propertyListSortOptions}
                    currentPropertyListType={currentPropertyListType}
                    setCurrentPropertyListType={setCurrentPropertyListType}
                />

                {currentPropertyListType === PropertyListType.TABLE && (
                    <OfferDetailPropertyListTable
                        filterValues={filterValues}
                        setFilterValues={setFilterValues}
                        propertyListObj={props.propertyListObj}
                        offerId={props.offerId}
                        offer={props.offer}
                        gtmData={{
                            listId: propertyListId,
                            listName: propertyListName
                        }}
                    />
                )}
                {currentPropertyListType === PropertyListType.TILES && (
                    <OfferDetailPropertyListTiles
                        filterValues={filterValues}
                        setFilterValues={setFilterValues}
                        propertyListObj={props.propertyListObj}
                        offer={props.offer}
                        gtmMeta={{
                            listId: propertyListId,
                            listName: propertyListName
                        }}
                    />
                )}
            </PropertyListWrapper>

            {hasPlanner3D && (
                <div css={buttonsWrapper}>
                    <Planner3dModal offer={props.offer} vendorId={props.offer.vendor.id} css={planner3dButtonStyle} routedModalId="detail">
                        <Button variant="filled_secondary">Zobacz makietę 3D</Button>
                    </Planner3dModal>
                    <ShowMoreButton variant="filled_secondary" onClick={() => setShowPropertyListBlend(false)} hide={!showPropertyListBlend}>
                        Pokaż więcej rzutów
                    </ShowMoreButton>
                </div>
            )}
        </>
    );
});

const PropertyListWrapper = styled.section<{showBlend: boolean}>`
    position: relative;
    z-index: 1;
    transition: max-height 0.4s ease-in-out;

    ${mb(3)};

    @media (min-width: ${(props) => props.theme.breakpoints.md}) {
        ${mb(3)};
    }

    ${(props) =>
        props.showBlend
            ? css`
                  max-height: 1300px;
                  overflow-y: hidden;

                  &:after {
                      content: "";
                      position: absolute;
                      bottom: 0;
                      left: 0;
                      width: 100%;
                      height: 10rem;
                      background: linear-gradient(rgba(255, 255, 255, 0), ${props.theme.colors.gray["100"]});
                  }

                  @media (min-width: ${props.theme.breakpoints.md}) {
                      max-height: 744px;
                  }
              `
            : css`
                  max-height: 8000px;

                  @media (min-width: ${props.theme.breakpoints.md}) {
                      max-height: 3000px;
                  }
              `};
`;

const buttonsWrapper = css`
    display: flex;
    gap: 2rem;
    justify-content: center;
`;

const planner3dButtonStyle = (theme: Theme) => css`
    display: flex;
    justify-content: center;
    ${mb(3)};

    @media (min-width: ${theme.breakpoints.md}) {
        ${mb(3)};
    }
`;

const ShowMoreButton = styled(Button)<{hide: boolean}>`
    display: ${(props) => (props.hide ? "none" : "block")};
`;
import React, {memo, useEffect, useState} from "react";
import {useDispatch} from "react-redux";
import {css, Theme} from "@emotion/react";
import styled from "@emotion/styled";

import {Button} from "@pg-design/button-module";
import {mb, pb} from "@pg-design/helpers-css";
import {Highlight, Text} from "@pg-design/text-module";
import {fromQueryValues, QueryValues} from "@pg-mono/form";
import {isEmpty, omit, pick} from "@pg-mono/nodash";
import {numberFormat} from "@pg-mono/string-utils";

import {useShouldShowPrice} from "../../../../hooks/use_should_show_price";
import {propertyListSortOptions} from "../../../../property/constants/property_list_sort_options";
import {IPropertyListObj} from "../../../../property_list/hooks/use_property_list";
import {ISearchFiltersFormValues} from "../../../../property_list/PropertyListFilters";
import {PropertyListType} from "../../../../property_list/PropertyListTabSwitcher";
import {Country} from "../../../../region/types/Country";
import {getLocalStoragePropertyFilters, updateLocalStoragePropertyFilters} from "../../../../search/utils/use_localstorage_property_filters";
import {FilterPresets, useUserPreferencesFilters} from "../../../../user/hooks/use_user_preferences_filters";
import {getUserPreferencesToOmit} from "../../../../user/utils/get_user_preferences_to_omit";
import {getCurrency} from "../../../../utils/get_currency";
import {getPlanner3dData} from "../../../constants/get_planner_3d_data";
import {getOfferTypeNamePlural} from "../../../helpers/OfferType";
import {IOfferDetail} from "../../../types/IOfferDetail";
import {OfferPriceType} from "../../../types/OfferPriceType";
import {OfferType} from "../../../types/OfferType";
import {fetchOfferDetailLowestPriceProperty} from "../../actions/fetch_offer_detail_lowest_price_property";
import {fetchOfferDetailProperties, PropertyListTabTypes} from "../../actions/fetch_offer_detail_properties";
import {OFFER_DETAIL_PROPERTY_LIST_DEFAULT_PAGE_SIZE} from "../../constants/offer_detail_properties";
import {Planner3dModal} from "../../planner3D/components/Planner3D";
import {OfferDetailPropertyListFilters} from "./OfferDetailPropertyListFilters";
import {OfferDetailPropertyListTable, propertyListTableFiltersFields} from "./OfferDetailPropertyListTable";
import {OfferDetailPropertyListTiles} from "./OfferDetailPropertyListTiles";

interface IProps {
    offerId: number;
    offer: IOfferDetail;
    propertyListObj: IPropertyListObj;
    onFilterValuesChange: (values: ISearchFiltersFormValues) => void;
    propertyListType?: PropertyListType;
    gtmMeta?: {
        listId: string;
        listName: string;
    };
}

export interface IMainImage {
    m_img_224x147?: string;
    m_img_375x211?: string;
    m_img_760x428?: string;
    m_img_1500?: string;
    m_img_155x87?: string;
}

export const initialPropertiesFilterValues: ISearchFiltersFormValues = {
    area: {lower: "", upper: ""},
    price: {lower: "", upper: ""},
    rooms: {lower: "", upper: ""},
    floor_choices: [],
    house_storeys: []
};

export const OfferDetailPropertyList: React.FC<IProps> = memo((props) => {
    const [currentPropertyListType, setCurrentPropertyListType] = useState(props.propertyListType ?? PropertyListType.TABLE);
    const isApartment = props.offer?.is_holiday_location || props.offer?.is_condohotel;
    const [filterValues, setFilterValues] = useState(initialPropertiesFilterValues);

    const hasPlanner3D = Boolean(getPlanner3dData(props.offer.id));
    const [showPropertyListBlend, setShowPropertyListBlend] = useState(hasPlanner3D);

    const dispatch = useDispatch();

    const userPreferencesFilters = useUserPreferencesFilters(FilterPresets.WithoutRegion);
    /*
     * filter values for property list - either based on user preferences or temporary filters from local storage
     */
    const localStoragePropertyFilters = getLocalStoragePropertyFilters();
    const initialQueryValues: QueryValues = isEmpty(localStoragePropertyFilters) ? userPreferencesFilters : localStoragePropertyFilters;

    /*
        Initial clientside data fetch
    */
    useEffect(() => {
        // set property list filters based on user preferences
        const clearedInitialQueryValue = pick(initialQueryValues, [
            "area_0",
            "area_1",
            "rooms_0",
            "rooms_1",
            "price_1",
            ...(props.offer.type === OfferType.FLAT ? ["floor_choices"] : []),
            ...(props.offer.type === OfferType.HOUSE ? ["house_storeys"] : [])
        ]);

        const searchFilters = fromQueryValues<ISearchFiltersFormValues>(propertyListTableFiltersFields, clearedInitialQueryValue);

        setFilterValues(searchFilters);

        if (props.offerId) {
            // fetch filtered properties only when initial filters has been set
            if (!isEmpty(initialQueryValues)) {
                const defaultQueryValues = {page: 1};
                const clearedInitialQueryValues = omit(initialQueryValues, getUserPreferencesToOmit(props.offer?.type));

                fetchOfferDetailProperties(props.offerId, PropertyListTabTypes.FILTER, OFFER_DETAIL_PROPERTY_LIST_DEFAULT_PAGE_SIZE, defaultQueryValues, {
                    ...clearedInitialQueryValues,
                    type: props.offer.type
                })(dispatch);

                dispatch(fetchOfferDetailLowestPriceProperty(props.offerId));
            }

            // clear ls stored filter after fetching, we don't want to reuse it
            updateLocalStoragePropertyFilters({});
        }
    }, []);

    useEffect(() => {
        props.onFilterValuesChange(filterValues);
    }, [filterValues]);

    const getOfferTypeName = () => {
        const offerTypeName = getOfferTypeNamePlural(props.offer.type);
        return offerTypeName.charAt(0).toUpperCase() + offerTypeName.slice(1);
    };

    const offerRangePrice =
        (props.offer.region.country === Country.SPAIN && props.offer?.stats?.ranges_price_min) || props.offer.stats?.ranges_price_m2_min || null;

    const priceType = props.offer.region.country === Country.SPAIN && props.offer.price_type === OfferPriceType.NETTO ? " netto" : "";

    const shouldShowPrice = useShouldShowPrice({
        offerPriceVisibility: props.offer.configuration.price_visibility,
        vendorPriceVisibility: props.offer.vendor.configuration.price_visibility,
        vendorId: props.offer.vendor.id
    });

    const propertyListHeadline = `${isApartment ? "Apartamenty" : getOfferTypeName()} w inwestycji`;
    const propertyListName = props.gtmMeta?.listName ?? propertyListHeadline;
    const propertyListId = props.gtmMeta?.listId ?? `${isApartment ? "apartamenty" : getOfferTypeName().toLowerCase()}_w_inwestycji`;

    return (
        <>
            <PropertyListWrapper showBlend={showPropertyListBlend && props.propertyListObj.properties?.length > 0}>
                {props.offer && (
                    <>
                        <Text variant="headline_4" as="h2" css={[pb(1)]}>
                            {propertyListHeadline}
                            {` (${props.offer.stats?.properties_count_for_sale})`}
                        </Text>

                        {shouldShowPrice &&
                            (props.offer.region.country === Country.SPAIN || props.offer.region.country === Country.POLAND) &&
                            offerRangePrice && (
                                <Text variant="headline_5" as="h3" css={[pb(2)]}>
                                    od{" "}
                                    <span className="bt">
                                        <Highlight>
                                            <Text variant="headline_2" as="span">{`${numberFormat(offerRangePrice)}${priceType} ${getCurrency({
                                                currency: props.offer.currency
                                            })}`}</Text>
                                            {props.offer.region.country === Country.SPAIN && props.offer.stats?.ranges_price_min ? null : (
                                                <>
                                                    /m<sup>2</sup>
                                                </>
                                            )}
                                        </Highlight>
                                    </span>
                                </Text>
                            )}
                    </>
                )}
                <OfferDetailPropertyListFilters
                    propertyListObj={props.propertyListObj}
                    filterValues={filterValues}
                    offerId={props.offerId}
                    offer={props.offer}
                    setFilterValues={setFilterValues}
                    sortOptions={propertyListSortOptions}
                    currentPropertyListType={currentPropertyListType}
                    setCurrentPropertyListType={setCurrentPropertyListType}
                />

                {currentPropertyListType === PropertyListType.TABLE && (
                    <OfferDetailPropertyListTable
                        filterValues={filterValues}
                        setFilterValues={setFilterValues}
                        propertyListObj={props.propertyListObj}
                        offerId={props.offerId}
                        offer={props.offer}
                        gtmData={{
                            listId: propertyListId,
                            listName: propertyListName
                        }}
                    />
                )}
                {currentPropertyListType === PropertyListType.TILES && (
                    <OfferDetailPropertyListTiles
                        filterValues={filterValues}
                        setFilterValues={setFilterValues}
                        propertyListObj={props.propertyListObj}
                        offer={props.offer}
                        gtmMeta={{
                            listId: propertyListId,
                            listName: propertyListName
                        }}
                    />
                )}
            </PropertyListWrapper>

            {hasPlanner3D && (
                <div css={buttonsWrapper}>
                    <Planner3dModal offer={props.offer} vendorId={props.offer.vendor.id} css={planner3dButtonStyle} routedModalId="detail">
                        <Button variant="filled_secondary">Zobacz makietę 3D</Button>
                    </Planner3dModal>
                    <ShowMoreButton variant="filled_secondary" onClick={() => setShowPropertyListBlend(false)} hide={!showPropertyListBlend}>
                        Pokaż więcej rzutów
                    </ShowMoreButton>
                </div>
            )}
        </>
    );
});

const PropertyListWrapper = styled.section<{showBlend: boolean}>`
    position: relative;
    z-index: 1;
    transition: max-height 0.4s ease-in-out;

    ${mb(3)};

    @media (min-width: ${(props) => props.theme.breakpoints.md}) {
        ${mb(3)};
    }

    ${(props) =>
        props.showBlend
            ? css`
                  max-height: 1300px;
                  overflow-y: hidden;

                  &:after {
                      content: "";
                      position: absolute;
                      bottom: 0;
                      left: 0;
                      width: 100%;
                      height: 10rem;
                      background: linear-gradient(rgba(255, 255, 255, 0), ${props.theme.colors.gray["100"]});
                  }

                  @media (min-width: ${props.theme.breakpoints.md}) {
                      max-height: 744px;
                  }
              `
            : css`
                  max-height: 8000px;

                  @media (min-width: ${props.theme.breakpoints.md}) {
                      max-height: 3000px;
                  }
              `};
`;

const buttonsWrapper = css`
    display: flex;
    gap: 2rem;
    justify-content: center;
`;

const planner3dButtonStyle = (theme: Theme) => css`
    display: flex;
    justify-content: center;
    ${mb(3)};

    @media (min-width: ${theme.breakpoints.md}) {
        ${mb(3)};
    }
`;

const ShowMoreButton = styled(Button)<{hide: boolean}>`
    display: ${(props) => (props.hide ? "none" : "block")};
`;
